/**
 * SmashBalloon Feed Monitor (DWA Internal → Web).
 *
 * Scans each client community homepage for the SmashBalloon "Custom Facebook
 * Feed" (cff) widget and reports whether it's rendering posts — so the Web
 * team can spot broken/empty feeds before clients notice. Client selector
 * (PLC first), problems surfaced first, then a grid grouped by community
 * group. Reads a daily snapshot.
 *
 * Detection note: expired-token errors are usually admin-only in the page
 * HTML, so a widget present with 0 posts ("empty") is our broken-signal.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchSmashBalloon,
  WEB_MONITOR_CLIENTS,
  type SmashBalloonResponse,
  type SmashBalloonSite,
  type BackendClientId,
} from '../services/dashboardApi';
import type { ScorecardTheme } from './ScorecardView';

export interface SmashBalloonProps {
  theme: ScorecardTheme;
}

// ─── Status → colour / label ──────────────────────────────────────────────

function statusMeta(status: string, theme: ScorecardTheme): { color: string; label: string; attention: boolean } {
  switch (status) {
    case 'ok': return { color: theme.positive, label: 'Displaying', attention: false };
    case 'empty': return { color: theme.negative, label: 'Empty — check feed', attention: true };
    case 'not_found': return { color: theme.textMuted, label: 'No feed on page', attention: false };
    case 'unreachable': return { color: theme.negative, label: 'Site unreachable', attention: true };
    default: return { color: theme.textMuted, label: 'Pending', attention: false };
  }
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return ''; }
}

// ─── Chips ────────────────────────────────────────────────────────────────

const Chip: React.FC<{ text: string; theme: ScorecardTheme; tone?: 'default' | 'accent' }> = ({ text, theme, tone = 'default' }) => (
  <span style={{
    fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.03em',
    padding: '0.1rem 0.4rem', borderRadius: 3,
    background: tone === 'accent' ? `${theme.accent}22` : theme.pageBg,
    color: tone === 'accent' ? theme.sectionTitle : theme.textSecondary,
    border: `1px solid ${theme.cardBorder}`, whiteSpace: 'nowrap',
  }}>{text}</span>
);

// ─── Site card ────────────────────────────────────────────────────────────

const SiteCard: React.FC<{ site: SmashBalloonSite; theme: ScorecardTheme }> = ({ site, theme }) => {
  const meta = statusMeta(site.status, theme);
  const latest = fmtDate(site.latest_post_at);
  return (
    <div style={{
      background: theme.cardBg,
      border: `1px solid ${meta.attention ? meta.color : theme.cardBorder}`,
      boxShadow: theme.cardShadow, borderRadius: 4, padding: '0.9rem 1rem',
      display: 'flex', flexDirection: 'column', gap: '0.55rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem' }}>
        {site.homepage_url ? (
          <a href={site.homepage_url} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: '0.92rem', fontWeight: 700, color: theme.textPrimary, textDecoration: 'none', lineHeight: 1.25 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = theme.accent)}
            onMouseLeave={(e) => (e.currentTarget.style.color = theme.textPrimary)}
          >{site.name} ↗</a>
        ) : (
          <span style={{ fontSize: '0.92rem', fontWeight: 700, color: theme.textPrimary }}>{site.name}</span>
        )}
        <span title={meta.label} style={{ flexShrink: 0, marginTop: 4, width: 9, height: 9, borderRadius: '50%', background: meta.color }} />
      </div>

      {(site.care_levels || site.respite) && (
        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
          {site.care_levels && <Chip text={site.care_levels} theme={theme} />}
          {site.respite && <Chip text="Respite" theme={theme} tone="accent" />}
        </div>
      )}

      <div style={{ borderTop: `1px solid ${theme.rowDivider}`, paddingTop: '0.5rem' }}>
        <div style={{ fontSize: '0.85rem', color: meta.color, fontWeight: 600 }}>{meta.label}</div>
        <div style={{ fontSize: '0.74rem', color: theme.textMuted, marginTop: '0.2rem' }}>
          {site.status === 'ok' && (
            <>{site.post_count ?? 0} post{site.post_count === 1 ? '' : 's'} shown{latest && <> · latest {latest}</>}</>
          )}
          {site.status === 'empty' && <>Widget present but no posts rendered</>}
          {site.status === 'not_found' && <>SmashBalloon widget not detected</>}
          {site.status === 'unreachable' && <>{site.error || `HTTP ${site.http_status ?? '—'}`}</>}
          {site.status === 'pending' && <>Awaiting first check</>}
        </div>
      </div>
    </div>
  );
};

// ─── Main view ────────────────────────────────────────────────────────────

export const SmashBalloonView: React.FC<SmashBalloonProps> = ({ theme }) => {
  const [client, setClient] = useState<BackendClientId>(WEB_MONITOR_CLIENTS[0].key);
  const [data, setData] = useState<SmashBalloonResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchSmashBalloon(client));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => { load(); }, [load]);

  const attention = useMemo(
    () => (data?.sites ?? []).filter((s) => s.status === 'empty' || s.status === 'unreachable'),
    [data],
  );
  const okCount = useMemo(() => (data?.sites ?? []).filter((s) => s.status === 'ok').length, [data]);

  // Group non-attention sites by community_group (attention shown separately on top).
  const groups = useMemo(() => {
    const out: { name: string; sites: SmashBalloonSite[] }[] = [];
    const idx = new Map<string, number>();
    for (const s of data?.sites ?? []) {
      const g = s.community_group || 'Other';
      if (!idx.has(g)) { idx.set(g, out.length); out.push({ name: g, sites: [] }); }
      out[idx.get(g)!].sites.push(s);
    }
    return out;
  }, [data]);

  const gridStyle: React.CSSProperties = {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.85rem',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: theme.textPrimary, margin: 0 }}>SmashBalloon Feed Monitor</h2>
          <p style={{ fontSize: '0.8rem', color: theme.textMuted, marginTop: '0.25rem' }}>
            Facebook feed render status per community site.
            {data && <> · {okCount}/{data.site_count} displaying{attention.length > 0 && <span style={{ color: theme.negative }}> · {attention.length} need attention</span>}</>}
            {data?.snapshot_at && <> · updated {new Date(data.snapshot_at).toLocaleString()}</>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select
            value={client}
            onChange={(e) => setClient(e.target.value as BackendClientId)}
            style={{
              fontSize: '0.82rem', padding: '0.35rem 0.55rem', borderRadius: 4,
              border: `1px solid ${theme.cardBorder}`, background: theme.cardBg,
              color: theme.textPrimary, fontFamily: 'inherit', cursor: 'pointer',
            }}
          >
            {WEB_MONITOR_CLIENTS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <button
            type="button" onClick={load} disabled={loading}
            style={{
              fontSize: '0.78rem', fontWeight: 600, padding: '0.4rem 0.85rem',
              borderRadius: 4, cursor: loading ? 'default' : 'pointer', fontFamily: 'inherit',
              background: 'transparent', color: theme.textSecondary,
              border: `1px solid ${theme.cardBorder}`, opacity: loading ? 0.6 : 1,
            }}
          >{loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>

      {error && (
        <div style={{
          background: theme.cardBg, border: `1px solid ${theme.negative}`,
          borderRadius: 4, padding: '1rem 1.25rem', color: theme.negative, fontSize: '0.85rem',
        }}>Failed to load SmashBalloon monitor: {error}</div>
      )}

      {!error && data && data.site_count === 0 && !loading && (
        <div style={{
          background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
          borderRadius: 4, padding: '2rem', textAlign: 'center', color: theme.textMuted,
        }}>No sites configured for this client yet.</div>
      )}

      {/* Needs attention — surfaced first */}
      {!error && attention.length > 0 && (
        <div>
          <h3 style={{
            fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
            textTransform: 'uppercase', color: theme.negative,
            margin: '0 0 0.6rem', paddingBottom: '0.4rem',
            borderBottom: `1px solid ${theme.negative}`,
          }}>Needs attention <span style={{ color: theme.textMuted }}>· {attention.length}</span></h3>
          <div style={gridStyle}>
            {attention.map((s) => <SiteCard key={s.site_id} site={s} theme={theme} />)}
          </div>
        </div>
      )}

      {/* All sites, grouped */}
      {!error && groups.map((group) => (
        <div key={group.name}>
          <h3 style={{
            fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
            textTransform: 'uppercase', color: theme.sectionTitle,
            margin: '0 0 0.6rem', paddingBottom: '0.4rem',
            borderBottom: `1px solid ${theme.sectionDivider}`,
          }}>{group.name} <span style={{ color: theme.textMuted }}>· {group.sites.length}</span></h3>
          <div style={gridStyle}>
            {group.sites.map((s) => <SiteCard key={s.site_id} site={s} theme={theme} />)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SmashBalloonView;
